import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import HeaderTitleContent from "../../common/components/atom/HeaderTitleContent/HeaderTitleContent";
import styles from './ClientV2Page.module.scss';
import ConsumptionChart from '../../features/Jouly/components/ComsumptionChart';
import Voltage from '../../features/Jouly/components/Voltage';
import Current from '../../features/Jouly/components/Current';
import Frequency from '../../features/Jouly/components/Frequency';
import { CreditChart } from '../../features/Jouly/components/Credit';
import { ActivePowerChart } from '../../features/Jouly/components/ActivePowerChart';
import PowerFactor from '../../features/Jouly/components/PowerFactor';
import LastValuesMetrics from './LastValuesMetrics';
import FilterV2 from '../../features/filter/components/FilterV2';
import { ActionPresenter } from '../../common/components/molecule/Action';
import { LogsListPresenter } from '../../common/components/molecule/LogsList';
import { PaymentTokensPresenter } from '../../common/components/molecule/PaymentTokens';
import { ClientInfo } from '../../features/meter/components';
import TagCellEditor from '../../common/components/molecule/TagCellEditor/TagCellEditor';
import {
  useMeterTagSingle,
  useUpdateMeterTag,
  useKnownMeterTags,
} from '../../features/meterTags/hooks/useMeterTags';
import { MomiStatusBadge, useDeviceCatalog } from '../../features/commandBus';
import { PermissionTooltip, useCapability } from '../../features/rbac';

function MeterTagsSection({ productNumber }: { productNumber: string }) {
  const tagQuery = useMeterTagSingle(productNumber)
  const updateMutation = useUpdateMeterTag()
  const canEditTags = useCapability('equipment.edit')
  const knownTags = useKnownMeterTags()
  const tags = tagQuery.data?.tags ?? []
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Tags :</span>
      <PermissionTooltip allowed={canEditTags}>
        <TagCellEditor
          tags={tags}
          onSave={(next) => updateMutation.mutateAsync({ productNumber, tags: next })}
          disabled={!canEditTags}
          compact={false}
          suggestions={knownTags}
        />
      </PermissionTooltip>
    </div>
  )
}

export default function ClientV2Page() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [selectedChart, setSelectedChart] = useState<{ component: React.ReactNode; title: string } | null>(null);
  // Catalogue command-bus pour le compteur (le backend route via dev_eui ; on
  // passe le deviceId de l'URL). Le badge MoMi et la modale Actions gèrent
  // eux-mêmes l'absence de catalogue / device non provisionné (404).
  const catalogQuery = useDeviceCatalog(deviceId);
  const momiId = catalogQuery.data?.momi_id;

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/main', { replace: false });
    }
  };

  const handleChartClick = (component: React.ReactNode, title: string) => {
    setSelectedChart({ component, title });
  };

  const closeModal = () => {
    setSelectedChart(null);
  };

  const handleViewOnPlanBT = async () => {
    if (!deviceId) return
    try {
      const backendURL = process.env.REACT_APP_BACKEND_API_URL + '/api/v1'
      const res = await axios.get<{ plan_id: string }>(`${backendURL}/planbt/find-by-meter/${deviceId}`)
      if (res.data?.plan_id) {
        navigate(`/main/plan-bt-v2/${res.data.plan_id}?meter=${deviceId}`)
        return
      }
    } catch (err) {
      console.warn('PlanBT lookup failed for meter', deviceId, err)
    }
    // Fallback: go to PlanBT list
    navigate('/main/plan-bt')
  }

  return (
    <div className={styles.clientV2Page}>
      <HeaderTitleContent 
        withGoBackBtn={true}
        goBackBtnLabel="Retour"
        onGoBack={handleGoBack}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Compteur {deviceId}</span>
            {momiId && <MomiStatusBadge momiId={momiId} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleViewOnPlanBT}
              style={{
                padding: '6px 12px',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: '#3b82f6',
                background: 'rgba(59, 130, 246, 0.08)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Voir sur le PlanBT
            </button>
            <ActionPresenter
              deviceId={deviceId}
              buttonSize="small"
              buttonVariant="secondary"
              buttonLabel="Actions"
            />
          </div>
        </div>
      </HeaderTitleContent>
      
      {/* Informations client */}
      {deviceId && <ClientInfo deviceId={deviceId} />}

      {deviceId && <MeterTagsSection productNumber={deviceId} />}

      <div className={styles.mainContent}>
        <div className={styles.leftContent}>
          <LastValuesMetrics />
          
          {/* FilterV2 - Position stratégique sous les métriques pour influencer les graphiques */}
          <div className={styles.filterSection}>
            <FilterV2 />
          </div>
          
          <div className={styles.chartsGrid}>
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<ConsumptionChart.ConsumptionAndIndexChart deviceId={deviceId} />, "Consommation")}
            >
              <ConsumptionChart.ConsumptionAndIndexChart deviceId={deviceId} />
            </div>
            
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<Voltage.Chart />, "Tension")}
            >
              <Voltage.Chart />
            </div>
            
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<Current.Chart />, "Courant")}
            >
              <Current.Chart />
            </div>
            
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<Frequency.Chart />, "Fréquence")}
            >
              <Frequency.Chart />
            </div>
            
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<CreditChart />, "Crédit")}
            >
              <CreditChart />
            </div>
            
            <div 
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<ActivePowerChart.Chart />, "Puissance Active")}
            >
              <ActivePowerChart.Chart />
            </div>
            
            <div
              className={styles.chartWrapper}
              onClick={() => handleChartClick(<PowerFactor.Chart />, "Facteur de Puissance")}
            >
              <PowerFactor.Chart />
            </div>
          </div>

        </div>

        <div className={styles.rightSidebar}>
          <div className={styles.sidebarSection}>
            <LogsListPresenter 
              deviceId={deviceId}
              maxHeight="300px"
              autoRefresh={true}
              refreshInterval={30000}
            />
          </div>
          
          <div className={styles.sidebarSection}>
            <PaymentTokensPresenter
              deviceId={deviceId}
              maxHeight="400px"
              autoRefresh={true}
              refreshInterval={60000}
            />
          </div>
        </div>
      </div>

      {/* Modal pour afficher le graphique en grand */}
      {selectedChart && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <div className={styles.modalFilterWrapper}>
                  <FilterV2 />
                </div>
              </div>
              <div className={styles.modalHeaderRight}>
                <button className={styles.closeButton} onClick={closeModal}>
                  ×
                </button>
              </div>
            </div>
            <div className={styles.modalBody}>
              {selectedChart.component}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
